import React, { useEffect, Suspense, lazy, createElement } from 'react';
import { Hero } from './components/Hero';
const StatsAndTestimonials = lazy(() => import('./components/StatsAndTestimonials').then(m => ({
  default: m.StatsAndTestimonials
})));
const PainPoints = lazy(() => import('./components/PainPoints').then(m => ({
  default: m.PainPoints
})));
const Modules = lazy(() => import('./components/Modules').then(m => ({
  default: m.Modules
})));
const ContentPreview = lazy(() => import('./components/ContentPreview').then(m => ({
  default: m.ContentPreview
})));
const ToolsIncluded = lazy(() => import('./components/ToolsIncluded').then(m => ({
  default: m.ToolsIncluded
})));
const Bonuses = lazy(() => import('./components/Bonuses').then(m => ({
  default: m.Bonuses
})));
const IsForYou = lazy(() => import('./components/IsForYou').then(m => ({
  default: m.IsForYou
})));
const Pricing = lazy(() => import('./components/Pricing').then(m => ({
  default: m.Pricing
})));
const FinalCTA = lazy(() => import('./components/FinalCTA').then(m => ({
  default: m.FinalCTA
})));
const Footer = lazy(() => import('./components/Footer').then(m => ({
  default: m.Footer
})));
const PurchaseToast = lazy(() => import('./components/PurchaseToast').then(m => ({
  default: m.PurchaseToast
})));
// URL-encoded parens so the preload <link> matches the <img> src exactly
// and the browser reuses the same network response (no double-fetch).
const HERO_IMAGE_URL = 'https://cdn.magicpatterns.com/uploads/6TLc9KtkH5TMhVgWdeFj3t/Diseno_sin_titulo_%284%29.webp';
export function App() {
  useEffect(() => {
    document.documentElement.lang = 'es-AR';
    document.title = 'Sistema Maestro de Aluminio™ — Todo tu taller de aluminio. Listo. Desde hoy.';
    // (Preconnect + hero image preload are handled synchronously in index.tsx
    // before React renders — no need to repeat that work here.)
    const metaDesc = document.querySelector('meta[name="description"]') || document.createElement('meta');
    metaDesc.setAttribute('name', 'description');
    metaDesc.setAttribute('content', 'Sistema Maestro de Aluminio: El sistema N°1 para carpinteros y armadores de aluminio en Argentina. Tablas de descuento, planos, mecanizados y presupuestos.');
    if (!document.querySelector('meta[name="description"]')) document.head.appendChild(metaDesc);
    if (!document.querySelector('meta[name="viewport"]')) {
      const viewport = document.createElement('meta');
      viewport.setAttribute('name', 'viewport');
      viewport.setAttribute('content', 'width=device-width, initial-scale=1');
      document.head.appendChild(viewport);
    }
    // Defer non-critical SEO/metadata + analytics setup so it never competes
    // with initial render/paint. These are not visible to users on first load.
    let jsonLd: HTMLScriptElement | null = null;
    let canonicalEl: HTMLLinkElement | null = null;
    let robotsEl: HTMLMetaElement | null = null;
    let pixelLoaded = false;
    const interactionEvents: Array<keyof DocumentEventMap> = ['pointerdown', 'keydown', 'touchstart'];
    const onFirstInteraction = () => {
      loadPixel();
      interactionEvents.forEach(ev => document.removeEventListener(ev, onFirstInteraction as any));
    };
    const handleCheckoutClick = (e: MouseEvent) => {
      const target = (e.target as HTMLElement)?.closest('a') as HTMLAnchorElement | null;
      if (target && target.href && target.href.includes('fegion.shop/cart')) {
        loadPixel();
        if ((window as any).fbq) {
          ;
          (window as any).fbq('track', 'InitiateCheckout');
        }
      }
    };
    const PIXEL_ID = '904978025482552';
    const loadPixel = () => {
      if (pixelLoaded || (window as any).fbq) return;
      pixelLoaded = true;
      const pixelScript = document.createElement('script');
      pixelScript.async = true;
      pixelScript.innerHTML = `
        !function(f,b,e,v,n,t,s)
        {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
        n.callMethod.apply(n,arguments):n.queue.push(arguments)};
        if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
        n.queue=[];t=b.createElement(e);t.async=!0;
        t.src=v;s=b.getElementsByTagName(e)[0];
        s.parentNode.insertBefore(t,s)}(window, document,'script',
        'https://connect.facebook.net/en_US/fbevents.js');
        fbq('init', '${PIXEL_ID}');
        fbq('track', 'PageView');
      `;
      document.head.appendChild(pixelScript);
      const pixelNoscript = document.createElement('noscript');
      const pixelImg = document.createElement('img');
      pixelImg.height = 1;
      pixelImg.width = 1;
      pixelImg.style.display = 'none';
      pixelImg.alt = '';
      pixelImg.src = `https://www.facebook.com/tr?id=${PIXEL_ID}&ev=PageView&noscript=1`;
      pixelNoscript.appendChild(pixelImg);
      document.head.appendChild(pixelNoscript);
    };
    const runDeferredSetup = () => {
      // OG tags
      const ogTags = [{
        property: 'og:title',
        content: 'Sistema Maestro de Aluminio™ — Todo tu taller de aluminio. Listo. Desde hoy.'
      }, {
        property: 'og:description',
        content: 'El sistema N°1 para carpinteros y armadores de aluminio en Argentina. Línea Herrero → Módena. Todas las tipologías.'
      }, {
        property: 'og:type',
        content: 'product'
      }, {
        property: 'og:image',
        content: HERO_IMAGE_URL
      }, {
        property: 'og:locale',
        content: 'es_AR'
      }];
      ogTags.forEach(({
        property,
        content
      }) => {
        const existing = document.querySelector(`meta[property="${property}"]`);
        if (!existing) {
          const meta = document.createElement('meta');
          meta.setAttribute('property', property);
          meta.setAttribute('content', content);
          document.head.appendChild(meta);
        }
      });
      // JSON-LD
      jsonLd = document.createElement('script');
      jsonLd.type = 'application/ld+json';
      jsonLd.textContent = JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'Product',
        name: 'Sistema Maestro de Aluminio™',
        description: 'El sistema N°1 para carpinteros y armadores de aluminio en Argentina. 6 Tomos + 6 Bonos.',
        image: HERO_IMAGE_URL,
        brand: {
          '@type': 'Brand',
          name: 'Sistema Maestro de Aluminio'
        },
        offers: [{
          '@type': 'Offer',
          name: 'Sistema Maestro de Aluminio',
          price: '19990',
          priceCurrency: 'ARS',
          availability: 'https://schema.org/InStock',
          url: '#pricing'
        }, {
          '@type': 'Offer',
          name: 'Sistema Maestro + Fichas de Taller',
          price: '29990',
          priceCurrency: 'ARS',
          availability: 'https://schema.org/InStock',
          url: '#pricing'
        }],
        aggregateRating: {
          '@type': 'AggregateRating',
          ratingValue: '4.9',
          reviewCount: '312'
        }
      });
      document.head.appendChild(jsonLd);
      // Canonical URL for SEO
      if (!document.querySelector('link[rel="canonical"]')) {
        canonicalEl = document.createElement('link');
        canonicalEl.rel = 'canonical';
        canonicalEl.href = window.location.origin + window.location.pathname;
        document.head.appendChild(canonicalEl);
      }
      // Robots meta
      if (!document.querySelector('meta[name="robots"]')) {
        robotsEl = document.createElement('meta');
        robotsEl.setAttribute('name', 'robots');
        robotsEl.setAttribute('content', 'index, follow');
        document.head.appendChild(robotsEl);
      }
      // Theme color
      if (!document.querySelector('meta[name="theme-color"]')) {
        const themeColor = document.createElement('meta');
        themeColor.setAttribute('name', 'theme-color');
        themeColor.setAttribute('content', '#0a0a0a');
        document.head.appendChild(themeColor);
      }
      // Attach pixel triggers
      interactionEvents.forEach(ev => document.addEventListener(ev, onFirstInteraction as any, {
        once: true,
        passive: true
      } as any));
      document.addEventListener('click', handleCheckoutClick);
    };
    // Prefetch lazy component chunks ASAP so below-the-fold renders instantly when scrolled to.
    // Kept separate from the heavier metadata setup so it can fire on its own (earlier) idle slot.
    const prefetchChunks = () => {
      import('./components/StatsAndTestimonials');
      import('./components/PainPoints');
      import('./components/Modules');
      import('./components/ContentPreview');
      import('./components/ToolsIncluded');
      import('./components/Bonuses');
      import('./components/IsForYou');
      import('./components/Pricing');
      import('./components/FinalCTA');
      import('./components/Footer');
      import('./components/PurchaseToast');
    };
    const ric: any = (window as any).requestIdleCallback || ((cb: any) => setTimeout(cb, 1));
    const cic: any = (window as any).cancelIdleCallback || ((id: any) => clearTimeout(id));
    // Start prefetching chunks almost immediately so scrolling feels instant.
    const prefetchId = ric(prefetchChunks, {
      timeout: 500
    });
    // Heavier SEO/analytics metadata can wait a bit longer; user can't see it.
    const idleId = ric(runDeferredSetup, {
      timeout: 1500
    });
    return () => {
      cic(idleId);
      cic(prefetchId);
      if (jsonLd) jsonLd.remove();
      if (canonicalEl) canonicalEl.remove();
      if (robotsEl) robotsEl.remove();
      interactionEvents.forEach(ev => document.removeEventListener(ev, onFirstInteraction as any));
      document.removeEventListener('click', handleCheckoutClick);
    };
  }, []);
  return <div className="min-h-screen bg-background text-gray-300 font-body selection:bg-gold selection:text-background" data-id="element-0">
      <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:bg-gold focus:text-background focus:px-4 focus:py-2 focus:rounded-lg focus:font-bold" data-id="element-1">
        Saltar al contenido principal
      </a>
      <main id="main-content" data-id="element-2">
        <Hero data-id="element-3" />
        <Suspense fallback={null} data-id="element-4">
          <StatsAndTestimonials data-id="element-5" />
          <PainPoints data-id="element-6" />
          <Modules data-id="element-7" />
          <ContentPreview data-id="element-8" />
          <ToolsIncluded data-id="element-9" />
          <Bonuses data-id="element-10" />
          <IsForYou data-id="element-11" />
          <Pricing data-id="element-12" />
          <FinalCTA data-id="element-13" />
        </Suspense>
      </main>
      <Suspense fallback={null} data-id="element-14">
        <Footer data-id="element-15" />
        <PurchaseToast data-id="element-16" />
      </Suspense>
    </div>;
}